import React, { useCallback, useMemo, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { AppRow } from '../../layout/appRow';
import { useSelector } from 'react-redux';
import { selectProfile } from '../../store/profileSlice';
import { format, isValid } from 'date-fns';
import { ColorPalette } from '../../styles/constants';
import { AppButtonOutlined } from '../app/button/appButtonOutlined';
import { ReportFilters, ViewMode, ReportSource } from '../../models/reports';

import {
  ActivityDetailedReport,
  InterdepReport,
  ProjectActivityReport,
  Report,
  SynthesisReport,
} from '../../models/report';
import { useUserRoles } from '../../hooks/useUserRoles';
import { RolesNames } from '../../models/employee';
import { generateProjectLabel } from '../../services/projectService';
import {
   formatDateOnly,
} from '../../services/dateService';

interface ExportDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  filters: ReportFilters;
  handleDownloadReportExcel: (report: Report) => Promise<void>;
  viewMode: ViewMode;
}

const styles = {
  dialog: {
    maxWidth: '530px',
    overflowX: 'hidden',
    position: 'relative',
  },
  topBox: {
    p: 3,
    background: ColorPalette.BACKGROUND,
  },
  inputDataBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    mb: 2,
  },
  inputValue: {
    color: ColorPalette.RAISIN_BLACK,
  },
  btn: {
    width: '200px',
  },
  btnBox: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    rowGap: 2,
  },
};

export function ExportDialog({
  isOpen,
  handleClose,
  filters,
  handleDownloadReportExcel,
  viewMode,
}: ExportDialogProps): JSX.Element {
  const profile = useSelector(selectProfile);
  const { isAdmin } = useUserRoles();
  const [isLoadingSynthesis, setIsLoadingSynthesis] = useState(false);
  const [isloadingActivity, setIsLoadingActivity] = useState(false);
  const [isLoadingProjectActivity, setIsLoadingProjectActivity] =
    useState(false);
  const [isLoadingInterdep, setIsLoadingInterdep] = useState(false);

  const {
    startDate,
    endDate,
    employeeName,
    managerName,
    projectName,
    projectCode,
    towerName,
  } = filters;

  const canViewInterdepButton = useMemo(
    () => viewMode === ViewMode.PEOPLE,
    [viewMode]
  );
  const canViewProjectActivitiesButton = useMemo(
    () => viewMode === ViewMode.PROJECT,
    [viewMode]
  );

  const managerLabel = useMemo(() => {
    switch (viewMode) {
      case ViewMode.PEOPLE:
        return RolesNames.TEAM_LEAD;

      case ViewMode.PROJECT:
        return RolesNames.PROJECT_MANAGER;

      default:
        return 'Manager';
    }
  }, [viewMode]);

  const formattedStartDate = useMemo(
    () =>
      isValid(new Date(formatDateOnly(startDate)))
        ? `${format(new Date(formatDateOnly(startDate)), 'dd/MM/yyy')}`
        : '',
    [startDate]
  );

  const formattedEndDate = useMemo(
    () =>
      isValid(new Date(formatDateOnly(endDate)))
        ? `${format(new Date(formatDateOnly(endDate)), 'dd/MM/yyy')}`
        : '',
    [endDate]
  );

  const projectLabel = useMemo(
    () => generateProjectLabel(projectName || 'ALL', projectCode || ''),
    [projectName, projectCode]
  );

  const synthesisReportDownloadCallback = useCallback(() => {
    setIsLoadingSynthesis(true);
    const filtersPayload = {
      ...filters,
      reportSource:
        viewMode === ViewMode.PEOPLE
          ? ReportSource.PEOPLE
          : ReportSource.PROJECT,
      showHierarchy:
        viewMode === ViewMode.PEOPLE ? filters.showHierarchy : false,
      includeDeleted:
        viewMode === ViewMode.PROJECT ? true : filters.includeDeleted,
      includeInactive:
        viewMode === ViewMode.PROJECT ? true : filters.includeInactive,
    };
    handleDownloadReportExcel(
      new SynthesisReport(filtersPayload, isAdmin)
    ).finally(() => setIsLoadingSynthesis(false));
  }, [filters, handleDownloadReportExcel, isAdmin, viewMode]);

  const activityReportDownloadCallback = useCallback(() => {
    setIsLoadingActivity(true);
    const filtersPayload = {
      ...filters,
      reportSource:
        viewMode === ViewMode.PEOPLE
          ? ReportSource.PEOPLE
          : ReportSource.PROJECT,
      showHierarchy:
        viewMode === ViewMode.PEOPLE ? filters.showHierarchy : false,
      includeDeleted:
        viewMode === ViewMode.PROJECT ? true : filters.includeDeleted,
      includeInactive:
        viewMode === ViewMode.PROJECT ? true : filters.includeInactive,
    };
    handleDownloadReportExcel(
      new ActivityDetailedReport(filtersPayload, isAdmin)
    ).finally(() => setIsLoadingActivity(false));
  }, [filters, handleDownloadReportExcel, isAdmin, viewMode]);

  const projectActivityReportDownloadCallback = useCallback(() => {
    setIsLoadingProjectActivity(true);
    const filtersPayload = {
      ...filters,
      showHierarchy: false,
    };
    handleDownloadReportExcel(
      new ProjectActivityReport(filtersPayload, isAdmin)
    ).finally(() => setIsLoadingProjectActivity(false));
  }, [filters, handleDownloadReportExcel, isAdmin]);

  const interdepReportDownloadCallback = useCallback(() => {
    setIsLoadingInterdep(true);
    handleDownloadReportExcel(new InterdepReport(filters, isAdmin)).finally(
      () => setIsLoadingInterdep(false)
    );
  }, [filters, handleDownloadReportExcel, isAdmin]);

  return (
    <Dialog open={isOpen} PaperProps={{ sx: styles.dialog }}>
      <AppDialogTitle
        title="Export"
        handleClose={handleClose}
        sx={{ position: 'sticky', top: 0 }}
      />
      <Box sx={styles.topBox}>
        <AppRow sx={styles.inputDataBox}>
          <Typography>
            {`${managerLabel}: `}
            <span style={styles.inputValue}>{`${
              managerName === null && isAdmin
                ? 'ALL'
                : managerName || profile?.name
            }`}</span>
          </Typography>
          <Typography>
            {`Engineer: `}
            <span style={styles.inputValue}>{`${employeeName || 'ALL'}`}</span>
          </Typography>
          <Typography>
            {`Project: `}
            <span style={styles.inputValue}>{projectLabel}</span>
          </Typography>
          <Typography>
            {`Tower: `}
            <span style={styles.inputValue}>{`${towerName || 'ALL'}`}</span>
          </Typography>
          <Typography>
            {`Begin date: `}
            <span style={styles.inputValue}>{formattedStartDate}</span>
          </Typography>
          <Typography>
            {`End date: `}
            <span style={styles.inputValue}>{formattedEndDate}</span>
          </Typography>
        </AppRow>
        <Box sx={styles.btnBox}>
          <AppButtonOutlined
            handleClick={synthesisReportDownloadCallback}
            isLoading={isLoadingSynthesis}
            hideIcon
            sx={styles.btn}
            label="Activity synthesis"
          />
          <AppButtonOutlined
            handleClick={activityReportDownloadCallback}
            isLoading={isloadingActivity}
            hideIcon
            sx={styles.btn}
            label="Activity detailed"
          />
          {canViewProjectActivitiesButton && (
            <AppButtonOutlined
              handleClick={projectActivityReportDownloadCallback}
              isLoading={isLoadingProjectActivity}
              hideIcon
              sx={styles.btn}
              label="Project activities"
            />
          )}
          {canViewInterdepButton && (
            <AppButtonOutlined
              handleClick={interdepReportDownloadCallback}
              isLoading={isLoadingInterdep}
              hideIcon
              sx={styles.btn}
              label="Interdep"
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
